import {
  Activity,
  Ban,
  BarChart3,
  BellRing,
  Building2,
  CalendarCheck,
  ClipboardList,
  FileBarChart,
  FolderKanban,
  Home,
  Landmark,
  Key,
  LayoutDashboard,
  Layers,
  Megaphone,
  Nfc,
  Receipt,
  RefreshCw,
  Route,
  Settings,
  Sparkles,
  Tags,
  Ticket,
  Truck,
  UserCog,
  UserPlus,
  Users,
  Wallet,
  Wifi,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@/modules/identity/application/ability';

export interface NavV2Item {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  path: string;
  permission?: Permission;
  comingSoon?: boolean;
  badgeKey?: string;
}

export interface NavV2Section {
  id: string;
  labelKey: string;
  defaultOpen?: boolean;
  items: NavV2Item[];
}

export const ADMIN_V2_SECTIONS: NavV2Section[] = [
  {
    id: 'principal',
    labelKey: 'menu.categories.main',
    defaultOpen: true,
    items: [
      { id: 'dashboard', labelKey: 'menu.v2.inicio', icon: Sparkles, path: '/admin-v2' },
    ],
  },
  {
    id: 'community',
    labelKey: 'menu.categories.community',
    defaultOpen: true,
    items: [
      { id: 'residentes', labelKey: 'menu.residentes', icon: Users, path: '/admin/residentes', permission: { action: 'read', subject: 'ResidentMenu' } },
      { id: 'unidades', labelKey: 'menu.unidades', icon: Home, path: '/admin/unidades', permission: { action: 'read', subject: 'Unit' } },
      { id: 'secciones', labelKey: 'menu.secciones', icon: Layers, path: '/admin/secciones', permission: { action: 'read', subject: 'Section' } },
      { id: 'reservas', labelKey: 'menu.reservas', icon: Building2, path: '/admin/reservas', permission: { action: 'read', subject: 'Reservation' } },
      { id: 'amenidades-reg', labelKey: 'menu.v2.amenidadesReg', icon: CalendarCheck, path: '/admin/amenidades', permission: { action: 'read', subject: 'Amenity' } },
      { id: 'incidentes', labelKey: 'menu.v2.incidentes', icon: Ticket, path: '/incidentes', badgeKey: 'incidentes', permission: { action: 'read', subject: 'Ticket' } },
    ],
  },
  {
    id: 'access',
    labelKey: 'menu.categories.access',
    defaultOpen: true,
    items: [
      { id: 'accesos', labelKey: 'menu.v2.actividad', icon: Activity, path: '/accesos', permission: { action: 'read', subject: 'AccessControl' } },
      { id: 'visitas', labelKey: 'menu.visitas', icon: UserPlus, path: '/registro-accesos', permission: { action: 'read', subject: 'AccessLog' } },
      { id: 'recorridos', labelKey: 'menu.v2.recorridos', icon: Route, path: '/recorridos', permission: { action: 'read', subject: 'GuardRoute' } },
    ],
  },
  {
    id: 'engagement',
    labelKey: 'menu.categories.engagement',
    defaultOpen: true,
    items: [
      { id: 'avisos', labelKey: 'menu.announcements', icon: Megaphone, path: '/admin/comunicados', badgeKey: 'avisos', permission: { action: 'read', subject: 'Announcement' } },
      { id: 'votaciones', labelKey: 'menu.polls', icon: BarChart3, path: '/encuestas', badgeKey: 'votaciones', permission: { action: 'read', subject: 'Poll' } },
      { id: 'transparencia', labelKey: 'menu.transparency', icon: FolderKanban, path: '/admin/transparencia', permission: { action: 'read', subject: 'TransparencyProject' } },
    ],
  },
  {
    id: 'finance',
    labelKey: 'menu.categories.finances',
    defaultOpen: false,
    items: [
      { id: 'finanzas', labelKey: 'menu.cuotas', icon: Landmark, path: '/admin/finanzas', permission: { action: 'read', subject: 'Charge' } },
      { id: 'pagos', labelKey: 'menu.v2.payments', icon: Wallet, path: '/admin/facturacion', permission: { action: 'read', subject: 'Payment' } },
      { id: 'gastos', labelKey: 'menu.v2.gastos', icon: Receipt, path: '/admin/egresos', permission: { action: 'read', subject: 'Expense' } },
      { id: 'cobranza', labelKey: 'menu.cobranza', icon: Ban, path: '/cobranza', permission: { action: 'read', subject: 'Cobranza' } },
      { id: 'reportes', labelKey: 'menu.reports', icon: FileBarChart, path: '/reportes', badgeKey: 'reportes', permission: { action: 'read', subject: 'Report' } },
      { id: 'resumen-finanzas', labelKey: 'menu.v2.resumenFinanzas', icon: LayoutDashboard, path: '/admin/resumen-finanzas', permission: { action: 'read', subject: 'Summary' } },
      { id: 'categorias', labelKey: 'menu.v2.categorias', icon: Tags, path: '/admin/categorias', permission: { action: 'read', subject: 'Category' } },
    ],
  },
  {
    id: 'operations',
    labelKey: 'menu.categories.operations',
    defaultOpen: false,
    items: [
      { id: 'personal', labelKey: 'menu.personal', icon: UserCog, path: '/admin/personal', permission: { action: 'read', subject: 'StaffMenu' } },
      { id: 'roles', labelKey: 'admin.card.roles', icon: Key, path: '/admin/roles', permission: { action: 'read', subject: 'RoleMenu' } },
      { id: 'proveedores', labelKey: 'menu.suppliers', icon: Truck, path: '/proveedores', permission: { action: 'read', subject: 'Supplier' } },
      { id: 'documentos', labelKey: 'menu.v2.documents', icon: ClipboardList, path: '/documentos', badgeKey: 'documentos', permission: { action: 'read', subject: 'Document' } },
      { id: 'routines', labelKey: 'menu.v2.routines', icon: RefreshCw, path: '/admin/routines', permission: { action: 'read', subject: 'Routine' } },
      // { id: 'auditoria', labelKey: 'menu.v2.auditoria', icon: FileBarChart, path: '/admin/auditoria' },
    ],
  },
  {
    id: 'configuration',
    labelKey: 'menu.categories.configuration',
    defaultOpen: false,
    items: [
      { id: 'puntos-acceso', labelKey: 'menu.v2.puntosAcceso', icon: Wifi, path: '/admin/puntos-acceso', permission: { action: 'read', subject: 'AccessDevice' } },
      { id: 'rfid', labelKey: 'menu.v2.rfid', icon: Nfc, path: '/admin/tarjetas-rfid', permission: { action: 'read', subject: 'RfidCredential' } },
      { id: 'notificaciones-config', labelKey: 'menu.v2.notificacionesReglas', icon: BellRing, path: '/admin/notificaciones-config', permission: { action: 'read', subject: 'NotificationConfig' } },
      { id: 'configuracion', labelKey: 'menu.v2.ajustes', icon: Settings, path: '/admin/configuracion', permission: { action: 'read', subject: 'Settings' } },
    ],
  },
];
